import { useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { makeStyles, Theme } from '@material-ui/core';
import { displayedName } from './displayed-name';
import { TruncatedText } from './TruncatedText';

const useStyles = makeStyles((theme: Theme) => ({
  // Stretched link: the ::after covers the whole row (the row is
  // position: relative), so clicking anywhere on it opens the entity. Other
  // links and buttons in the row sit above it with z-index: 1.
  link: {
    fontWeight: 500,
    lineHeight: '20px',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
    },
  },
  // Above the stretched link's ::after so it receives the hover that shows
  // the full text. It is a second link to the entity, so clicks keep their
  // native behaviour (new tab, context menu).
  description: {
    position: 'relative',
    zIndex: 1,
    fontSize: 12,
    '& a': { color: 'inherit', textDecoration: 'none' },
    // Explicit: the table cell's weight would otherwise be inherited.
    fontWeight: 400,
    lineHeight: '16px',
    color: theme.palette.text.secondary,
  },
  empty: {
    fontSize: 12,
    // Explicit: the table cell's weight would otherwise be inherited.
    fontWeight: 400,
    lineHeight: '16px',
    color: theme.palette.text.secondary,
  },
}));

export function NameCell({ entity }: { entity: Entity }) {
  const classes = useStyles();
  const description = entity.metadata.description?.trim();
  // Keyboard focus on the name link also reveals a cut-off description,
  // which has no tab stop of its own.
  const [focused, setFocused] = useState(false);
  return (
    <div onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}>
      <TruncatedText>
        <EntityRefLink
          entityRef={entity}
          title={displayedName(entity)}
          hideIcon
          className={classes.link}
        />
      </TruncatedText>
      {description ? (
        <TruncatedText className={classes.description} revealed={focused}>
          {/* Duplicate of the name link: out of the tab order and hidden
              from screen readers, which already have the name link. */}
          <EntityRefLink
            entityRef={entity}
            hideIcon
            disableTooltip
            tabIndex={-1}
            aria-hidden
          >
            {description}
          </EntityRefLink>
        </TruncatedText>
      ) : (
        <div className={classes.empty}>No description</div>
      )}
    </div>
  );
}
